/**
 * Tolkar stryktipset som det kommer från rättaren.
 *
 * Formatet är inte definierat någonstans — det är hur Carl råkar skriva, och
 * det ska det få fortsätta vara. Parsern anpassar sig i stället:
 *
 *   Torsdag:                      ← dagrubrik, hoppas över
 *
 *   1 Tommy Fleetwood på första hålet…
 *   1 Träffar både fairway och green
 *   X Missar både fairway och green
 *   2 Träffar antingen fairway eller green
 *
 *   —----------                   ← avdelare, hoppas över
 *
 *   Utslagsfråga: Vilken placering får Haotong Li?
 *
 * Den svåra biten är att "1 …" kan vara både en frågas nummer och ett
 * svarsalternativ. Det som skiljer dem är sammanhanget: en ny fråga har det
 * nummer som står på tur OCH kommer när föregående fråga fått sina tre
 * alternativ. Allt annat som inleds med 1, X eller 2 är ett alternativ.
 *
 * Fråga 6 i det verkliga underlaget numrerade alternativen 1/2/3 i stället
 * för 1/X/2. Databasen känner bara 1/X/2, så alternativen mappas efter
 * ordning och inte efter tecknet — och det rapporteras, så ingen tror att
 * något tolkats bokstavligt.
 */

export interface ParsedQuestion {
  q_no: number
  label: string
  opt_1: string
  opt_x: string
  opt_2: string
  /** Källan skrev 1/2/3 i stället för 1/X/2. Alternativen togs i ordning. */
  renumbered: boolean
}

export interface ParsedTips {
  questions: ParsedQuestion[]
  tiebreakLabel: string | null
  /** Sådant som gick att tolka men som någon bör titta på innan det sparas. */
  warnings: string[]
}

/** "1 Text", "X. Text", "3) Text" — nyckeln följt av texten. */
const OPTION = /^([1X2x3])[.):]?\s+(\S.*)$/
/** "7 Frågans text" — numret följt av texten. */
const QUESTION = /^(\d{1,2})[.):]?\s+(\S.*)$/
const TIEBREAK = /^utslagsfr[åa]ga\s*[:\-–—]?\s*(.*)$/i
/** "—----------", "---", "–—-" — bara streck och blanksteg. */
const SEPARATOR = /^[\s\-–—_=*]+$/

interface Building {
  q_no: number
  label: string
  keys: string[]
  texts: string[]
}

export function parseStryktips(input: string): ParsedTips {
  const warnings: string[] = []
  const questions: ParsedQuestion[] = []
  let tiebreakLabel: string | null = null

  let current: Building | null = null
  /*
   * Numret på frågan som står på tur.
   *
   * Räknas inte ur questions.length: den frågan som just byggs är inte
   * pushad än, så mitt i fråga 1 hade "nästa" fortfarande sagt 1 — och då
   * lästes "2 Ledarscore efter 18 hål?" som ett svarsalternativ i stället
   * för som fråga 2.
   */
  let expected = 1
  /*
   * Sant direkt efter en tom rad, en avdelare eller en dagrubrik. En ny
   * fråga inleds alltid efter en sådan; ett svarsalternativ aldrig.
   *
   * Används bara när tolkningen redan spårat ur. I ett helt underlag räcker
   * numret på tur, och kravet på avgränsning skulle bara göra parsern
   * känslig för hur många tomrader någon råkat lämna.
   */
  let boundary = true
  /** Sant när en rad inte gick att placera. Då är numret inte att lita på. */
  let desynced = false

  const finish = () => {
    if (!current) return

    if (current.texts.length !== 3) {
      warnings.push(
        `Fråga ${current.q_no} fick ${current.texts.length} svarsalternativ i stället för tre.`,
      )
    }

    // Efter ordning, inte efter tecken. Se rubrikkommentaren om fråga 6.
    const renumbered = current.keys.length === 3 && !current.keys.some((k) => k.toUpperCase() === 'X')

    questions.push({
      q_no: current.q_no,
      label: current.label,
      opt_1: current.texts[0] ?? '1',
      opt_x: current.texts[1] ?? 'X',
      opt_2: current.texts[2] ?? '2',
      renumbered,
    })

    current = null
  }

  for (const raw of input.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || SEPARATOR.test(line)) { boundary = true; continue }

    const tb = TIEBREAK.exec(line)
    if (tb) {
      finish()
      // Står frågan på raden efter rubriken tas den i nästa varv.
      tiebreakLabel = tb[1]?.trim() || null
      continue
    }

    // Dagrubrik: "Fredag:", "Söndag" — text utan inledande nyckel. De bär
    // ingen information tipset behöver och räknas som avgränsning.
    if (!OPTION.test(line) && !QUESTION.test(line)) {
      boundary = true
      continue
    }

    const q = QUESTION.exec(line)
    const atBoundary = boundary
    boundary = false

    // Ny fråga: rätt nummer på tur, och föregående är komplett. Har något
    // redan spårat ur krävs dessutom en avgränsning före — annars kan ett
    // svarsalternativ vars siffra råkar stämma befordras till fråga.
    if (
      q &&
      Number(q[1]) === expected &&
      (!current || current.texts.length === 3) &&
      (!desynced || atBoundary)
    ) {
      finish()
      current = { q_no: expected, label: q[2]!.trim(), keys: [], texts: [] }
      expected++
      desynced = false
      continue
    }

    const o = OPTION.exec(line)
    if (o && current && current.texts.length < 3) {
      current.keys.push(o[1]!)
      current.texts.push(o[2]!.trim())
      continue
    }

    desynced = true
    if (!current) {
      warnings.push(`Raden hörde inte till någon fråga och hoppades över: "${line}"`)
    } else {
      warnings.push(`Fråga ${current.q_no} hade en rad för mycket: "${line}"`)
    }
  }

  finish()

  const renumbered = questions.filter((q) => q.renumbered).map((q) => q.q_no)
  if (renumbered.length > 0) {
    warnings.push(
      `Fråga ${renumbered.join(', ')} numrerade alternativen 1/2/3 i stället för 1/X/2 — ` +
        'troligen en felskrivning. Alternativen togs i ordning, så mittenraden blev X. ' +
        'Kontrollera att det stämmer.',
    )
  }

  if (questions.length === 0) {
    warnings.push('Ingen fråga hittades. Kontrollera att varje fråga inleds med sitt nummer.')
  } else if (questions.length !== 18) {
    warnings.push(`${questions.length} frågor tolkades, inte 18.`)
  }

  if (!tiebreakLabel) {
    warnings.push('Ingen utslagsfråga hittades. Raden ska börja med "Utslagsfråga:".')
  }

  return { questions, tiebreakLabel, warnings }
}
